import type { SharedPanelView } from "@panelgenie/domain";
import { Badge, Banner, Card, Heading } from "@panelgenie/shared-ui";

/**
 * Read-only landing page for a `SharedPanelView` — the panel-scope
 * counterpart to `SharedSiteLanding`.
 *
 * Renders the projection as a labeled circuit list the recipient can
 * scroll through. R-SAFE-3 advisory rides above the circuit list
 * when the panel matches the dangerous-panel registry.
 *
 * Per the `SharedDeviceSummary` projection contract, device
 * nameplate exposes only `manufacturer` + `model` — serial and year
 * are NOT in the projection (privacy + replacement-fraud hardening).
 *
 * Read-only by construction.
 */
export type SharedPanelLandingProps = {
  readonly view: SharedPanelView;
};

export const SharedPanelLanding = ({ view }: SharedPanelLandingProps) => {
  const { site, panel, safetyAdvisory, disclaimer } = view;
  return (
    <article
      aria-label="shared-panel-landing"
      className="mx-auto max-w-3xl px-4 py-6 text-fg"
    >
      <header>
        <p className="m-0 text-xs uppercase tracking-wider text-mute">
          Shared by Panel Genie — view only
        </p>
        <Heading level="h1" className="mt-1">
          {panel.manufacturer ?? "Unknown manufacturer"}
          {panel.model !== undefined ? ` ${panel.model}` : ""}
        </Heading>
        <p className="mt-1 text-base text-mute">
          {site.name} — {site.address.city}, {site.address.region}
        </p>
        <p className="mt-1 text-sm text-mute">
          {panel.columns} × {panel.rows} positions ·{" "}
          {panel.mainAmperage !== undefined
            ? `${panel.mainAmperage} A main`
            : "Main amperage not recorded"}{" "}
          · {panel.orientation} orientation
        </p>
      </header>

      {safetyAdvisory !== undefined ? (
        <Banner
          variant="warning"
          aria-label="shared-panel-safety-advisory"
          title={`Safety advisory — ${safetyAdvisory.entry.displayName}`}
          className="mt-4"
        >
          <p className="m-0">{safetyAdvisory.entry.concern}</p>
          <p className="mt-1 m-0 text-xs text-mute">
            Source: {safetyAdvisory.entry.source.label}
          </p>
        </Banner>
      ) : null}

      <section aria-label="shared-panel-circuits" className="mt-6">
        <Heading level="h2" className="mb-3">
          Circuits
        </Heading>
        {panel.circuits.length === 0 ? (
          <p className="text-base text-mute">
            No circuits have been labeled on this panel yet.
          </p>
        ) : (
          <ol className="list-none p-0 m-0 flex flex-col gap-2">
            {panel.circuits.map((c) => (
              <li key={c.circuitId}>
                <Card aria-label={`shared-circuit-${c.circuitId}`}>
                  <Card.Body className="flex items-baseline gap-3">
                    <span className="font-semibold min-w-[3rem] text-mute">
                      {c.identifier ?? c.headPositionNumber}
                    </span>
                    <span className="flex-1 text-base">
                      {c.effectiveLegendText}
                    </span>
                    {c.criticalLoad ? (
                      <Badge
                        variant="danger"
                        aria-label={`shared-circuit-${c.circuitId}-critical`}
                      >
                        Critical
                      </Badge>
                    ) : null}
                    <span className="text-xs text-dim min-w-[5rem] text-right">
                      {c.status}
                    </span>
                  </Card.Body>
                </Card>
              </li>
            ))}
          </ol>
        )}
        {panel.unusedPositionNumbers.length > 0 ? (
          <p
            aria-label="shared-panel-unused-positions"
            className="mt-3 text-xs text-dim"
          >
            Unused positions: {panel.unusedPositionNumbers.join(", ")}
          </p>
        ) : null}
      </section>

      <Banner
        variant="info"
        aria-label="shared-panel-disclaimer"
        title="Safety disclaimer"
        className="mt-6"
      >
        {disclaimer.body}
      </Banner>
    </article>
  );
};
